import { Menu } from '@base-ui/react/menu'

import { Icon } from './Icon'

/**
 * One action that has variants: the thing people mostly want, and its near neighbours behind a
 * chevron.
 *
 * *Write a document* is the case it exists for. Most of the time the answer is "yes, a document, in
 * the language I always write in" — one press — and occasionally it is "in one of the others", which
 * is a second press and not a decision anybody has to make first.
 *
 * **Why not one button per variant.** That is a row of buttons standing in for a choice, which the
 * design chapter refuses, and it grows a column in the area's head every time a language is added.
 * With three markups it would be three primary-looking controls where there is one action.
 *
 * **Why not a select beside the button.** The default stays one press. Choosing is the exception, and
 * the exception is what goes behind the chevron rather than in front of the action.
 *
 * Base UI provides the menu — keyboard, focus, the popup staying on screen — and everything visible
 * is MyLib's, built from the tokens.
 */
export interface ComboButtonAction {
  readonly id: string
  readonly label: string
}

export interface ComboButtonProps {
  /** What the main half says and does. */
  readonly label: string
  readonly onAct: () => void
  /**
   * The near neighbours, behind the chevron. **The default action is not repeated here**: it is the
   * button, and a menu that lists it again asks somebody to choose what they already chose.
   */
  readonly actions: readonly ComboButtonAction[]
  readonly onChoose: (id: string) => void
  /** Names the chevron for a screen reader: "Other languages". */
  readonly moreLabel: string
  readonly tone?: 'primary' | 'quiet'
  readonly disabled?: boolean
}

export function ComboButton({
  label,
  onAct,
  actions,
  onChoose,
  moreLabel,
  tone = 'quiet',
  disabled = false,
}: ComboButtonProps) {
  return (
    <div className={`combo combo--${tone}`}>
      <button type="button" className="combo__main" disabled={disabled} onClick={onAct}>
        {label}
      </button>
      <Menu.Root>
        <Menu.Trigger
          className="combo__more"
          aria-label={moreLabel}
          title={moreLabel}
          disabled={disabled}
        >
          <Icon name="down" />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner className="combo__positioner" sideOffset={4} align="end">
            <Menu.Popup className="combo__popup">
              {actions.map((action) => (
                <Menu.Item
                  key={action.id}
                  className="combo__item"
                  onClick={() => {
                    onChoose(action.id)
                  }}
                >
                  {action.label}
                </Menu.Item>
              ))}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </div>
  )
}
